import { GlobalWorkerOptions } from 'pdfjs-dist';

/**
 * Polyfill: Map.prototype.getOrInsertComputed
 *
 * pdf.js v5 bygger på den här metoden (9 anrop i huvudbygget, 6 i workern).
 * Den är mycket ny och saknas i äldre Safari — bland annat på iOS-versioner
 * som fortfarande används i fält. Utan polyfill kastas
 * "getOrInsertComputed is not a function" och HELA PDF-visningen dör.
 *
 * Rapporterat 2026-08-04: yrkesarbetare på iPhone kunde inte läsa
 * arbetsmiljödokument vid inskrivning. Träffar även dokumentmodulen,
 * kvalitetsronder, dagbokskartan och Inköps offertvisare — alla går via
 * den här filen.
 *
 * Källkoden ligger som sträng för att kunna injiceras i worker-tråden
 * också; workern är en separat JS-miljö och ärver inte main-trådens patchar.
 */
const MAP_POLYFILL_SRC = `
if (typeof Map !== "undefined" && !Map.prototype.getOrInsertComputed) {
  Object.defineProperty(Map.prototype, "getOrInsertComputed", {
    value: function (key, callbackFn) {
      if (!this.has(key)) this.set(key, callbackFn(key));
      return this.get(key);
    },
    writable: true, configurable: true, enumerable: false
  });
}
if (typeof WeakMap !== "undefined" && !WeakMap.prototype.getOrInsertComputed) {
  Object.defineProperty(WeakMap.prototype, "getOrInsertComputed", {
    value: function (key, callbackFn) {
      if (!this.has(key)) this.set(key, callbackFn(key));
      return this.get(key);
    },
    writable: true, configurable: true, enumerable: false
  });
}
`;

// Mät stödet INNAN vi patchar — worker-tråden behöver samma besked, och efter
// patchen ser main-tråden alltid ut att ha metoden.
const NATIVE_SUPPORT = (() => {
  try {
    return typeof Map !== 'undefined' && typeof Map.prototype.getOrInsertComputed === 'function';
  } catch (e) {
    return false;
  }
})();

// Applicera i main-tråden. Skrivs ut direkt (inte via strängen ovan) — att
// evaluera kod i runtime vore onödigt och sämre för CSP.
function definePolyfill(proto) {
  if (!proto || typeof proto.getOrInsertComputed === 'function') return;
  Object.defineProperty(proto, 'getOrInsertComputed', {
    value: function (key, callbackFn) {
      if (!this.has(key)) this.set(key, callbackFn(key));
      return this.get(key);
    },
    writable: true,
    configurable: true,
    enumerable: false,
  });
}
if (typeof Map !== 'undefined') definePolyfill(Map.prototype);
if (typeof WeakMap !== 'undefined') definePolyfill(WeakMap.prototype);

let pdfjsVersion = '';
try {
  // CRA supports JSON imports; this lets us pick a matching pdf.js worker from CDN.
  // eslint-disable-next-line import/no-webpack-loader-syntax
  // eslint-disable-next-line global-require
  pdfjsVersion = require('pdfjs-dist/package.json')?.version || '';
} catch (e) {
  pdfjsVersion = '';
}

let workerSrc = '';

// Force a reliable worker URL.
// We intentionally avoid cdnjs here: it often lacks the exact worker build for newer pdf.js versions.
// pdfjs-dist v5 uses an ESM worker; serving the matching .mjs file avoids the "fake worker" fallback failing.
if (pdfjsVersion) {
  workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;
} else {
  // Fallback without a pinned version (best-effort)
  workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist/build/pdf.worker.min.mjs';
}

/**
 * På browsers utan nativt stöd räcker det inte att patcha main-tråden —
 * worker-koden kraschar på egen hand. Vi bygger därför samma blob-wrapper
 * som pdf.js själv använder för cross-origin-workers, men lägger polyfillen
 * före importen. Blobben är same-origin, så pdf.js laddar den rakt av.
 *
 * Går det inte (ingen Blob/URL, eller CSP som blockerar blob:-workers) faller
 * vi tillbaka på CDN-URL:en. pdf.js har då en sista utväg i sin "fake worker",
 * som kör i main-tråden där polyfillen redan sitter.
 */
function buildPolyfilledWorkerSrc(cdnUrl) {
  try {
    if (typeof Blob === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
      return cdnUrl;
    }
    // JSON.stringify på en URL vi själva konstruerat — ingen användardata.
    const wrapper = `${MAP_POLYFILL_SRC}\nawait import(${JSON.stringify(cdnUrl)});`;
    return URL.createObjectURL(new Blob([wrapper], { type: 'text/javascript' }));
  } catch (e) {
    return cdnUrl;
  }
}

// In Jest/unit tests, GlobalWorkerOptions may be undefined in mocks.
// Guarding this keeps tests from crashing while preserving runtime behavior.
if (workerSrc && GlobalWorkerOptions) {
  GlobalWorkerOptions.workerSrc = NATIVE_SUPPORT ? workerSrc : buildPolyfilledWorkerSrc(workerSrc);
}
